// CATEGORY HUE — a user-chosen colour is one of nine NAMES, never a hex
// (DEV-GUIDE-002 §12.5 "User-chosen colour"; ADR-001 Law F5; owner decision
// D2, taken 2026-09-19, batch 17 — audit COL-06 / L5 / C-23).
//
// Before D2 six lib columns and two cms shapes stored whatever a `<input
// type=color>` or a Tailwind-v3 swatch palette produced, and the screens
// painted that hex raw in both themes (a #2563eb bar is the same bar in dark).
// A hue NAME resolves per theme on screen (`@erp/ui/category-hue` PAIR) and to
// a static light stop in print (STATIC_900), so the value the row stores is a
// CATEGORY, not a pixel.
//
// WHY HERE and not in the kit: `modules/lib/schemas` and `modules/cms/schemas`
// both validate the wire with it, and a schemas lane is zod-only and may not
// reach `@erp/ui` (browser code). Used by two modules → `packages/`
// (DEV-GUIDE-001 §3). `@erp/ui/category-hue` derives its `CategoryHue` type
// and its `hueForName` from THIS file, so the nine names and the one hash have
// exactly one owner.
//
// APPEND-ONLY, in this order: `hueForName` indexes into the tuple, and the
// db-lib / db-cms nearest-hue migrations (lib 0022, cms 0021) re-state the same
// hash in SQL for rows whose legacy hex matched no known palette — reordering
// or inserting a hue would re-colour every hashed row and chip in the wild.
import { z } from "zod";

/** The nine non-semantic hues of DEV-GUIDE-002 §12.5, in base.css order. */
export const CATEGORY_HUES = [
  "indigo",
  "purple",
  "fuchsia",
  "magenta",
  "yellow",
  "celery",
  "chartreuse",
  "cyan",
  "seafoam",
] as const;

/** A stored category colour: one of the nine hue names. Validate every colour column and colour field with this — never a hex grammar. */
export const CategoryHue = z.enum(CATEGORY_HUES);
export type CategoryHue = z.infer<typeof CategoryHue>;

/**
 * Deterministic hue for a free-form category name (a subject, a tag, a
 * person's name for an avatar fallback) — ONE hash for the whole repo. Trimmed
 * and lower-cased first, so "Philosophy", " philosophy" and "PHILOSOPHY" agree
 * on every screen. The db migrations that retired stored hex re-state this
 * exact arithmetic in SQL (`(h * 31 + code) mod 2^32`, then `mod 9`), so a row
 * whose hex matched no palette wears the same hue its name would hash to here.
 */
export function hueForName(name: string): CategoryHue {
  const s = name.trim().toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return CATEGORY_HUES[h % CATEGORY_HUES.length]!;
}
